import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import { InvestmentTypeEnum } from "@rentablo/api/apiv1";
import { PageLayout } from "@components";
import { LoadingProgressBar } from "@rentablo/components";
import { manualTransactionWizardSM } from "./stateMachine/manualTransactionWizardSM";
import { useStateHelpers } from "./hooks/useStateHelpers";
import { InvestmentTypeStep } from "./steps/InvestmentTypeStep";
import { NewTransactionSteps } from "./NewTransactionSteps";
import { defaultContext } from "./stateMachine/types/transactionWizardContext";

export interface Props {
  depotId?: number;
  type?: InvestmentTypeEnum | null;
  initialCurrency: string;
}

export const ManualTransactionWizard = ({
  depotId,
  type,
  initialCurrency,
}: Props) => {
  const { t } = useTranslation();
  const [currentState, sendEvent] = useMachine(manualTransactionWizardSM, {
    context: {
      bookingValues: {
        ...defaultContext.bookingValues,
        accountCurrency: initialCurrency,
      },
    },
  });
  const { isLoadingState } = useStateHelpers(currentState);

  return (
    <PageLayout
      breadcrumbs={[
        { text: t("breadcrumbs.home"), to: "/" },
        { to: "/investments", text: t("breadcrumbs.investments") },
        { text: t("transactionWizard.manual.title") },
      ]}
    >
      {isLoadingState() && <LoadingProgressBar />}

      {currentState.matches("idle") &&
        type &&
        sendEvent({
          type: "INIT_FROM_BOOKING_TYPE",
          depotId: depotId,
          investmentType: type,
        }) && <LoadingProgressBar />}

      {currentState.matches("idle") &&
        !type &&
        sendEvent({ type: "INIT_FROM_INVESTMENT_TYPE", depotId: depotId }) && (
          <LoadingProgressBar />
        )}

      {currentState.matches("selectInvestmentType") && (
        <InvestmentTypeStep
          handleNext={(investmentType: InvestmentTypeEnum) => {
            sendEvent({ type: "INVESTMENT_TYPE_SELECTED", investmentType });
          }}
          handlePrevious={() => window.history.back()}
        />
      )}

      <NewTransactionSteps
        currentState={currentState}
        sendEvent={sendEvent}
        initFromNewTransaction={Boolean(type)}
        isManualTransaction
      />
    </PageLayout>
  );
};
